(() => {
  const RESET_ID = "tmr-variant-reset";
  const STYLE_ID = "tmr-variant-reset-style";
  const INIT_ATTR = "data-tmr-variant-init";

  let isInternalChange = false;
  let syncTimer = null;

  function injectResetStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      #${RESET_ID}{
        display:inline-flex;align-items:center;justify-content:center;gap:6px;
        padding:8px 12px;border-radius:999px;
        border:1px solid rgba(0,0,0,0.10);
        background:#f2f2f2;color:inherit;font-size:13px;line-height:1;
        cursor:pointer;transition:background 120ms ease,border-color 120ms ease,transform 120ms ease;
        user-select:none;margin-top:10px;
      }
      #${RESET_ID}:hover{background:#f5f5f5;border-color:rgba(0,0,0,0.16)}
      #${RESET_ID}:active{transform:translateY(1px)}
      #${RESET_ID} .tmr-reset-label{font-weight:600;letter-spacing:.2px}
      #${RESET_ID} .tmr-reset-sub{font-weight:500;opacity:.7}
    `;
    document.head.appendChild(style);
  }

  function getVariants(root) {
    const script = root.querySelector('script[type="application/json"]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  function getSelects(root) {
    return Array.from(root.querySelectorAll("select"));
  }

  function dispatch(select) {
    select.dispatchEvent(new Event("input", { bubbles: true }));
    select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function dispatchAll(selects) {
    // Dawn tends to behave best if changes are emitted in option order.
    selects.forEach(dispatch);
  }

  function getSelections(selects) {
    return selects.map(select => select.value);
  }

  function isFullSelection(selections) {
    return selections.every(value => value && value !== "");
  }

  function variantOptions(variant, optionCount) {
    return [variant.option1, variant.option2, variant.option3].slice(0, optionCount);
  }

  function findExactVariant(variants, selections, optionCount) {
    if (!isFullSelection(selections)) return null;

    return (
      variants.find(variant => {
        const options = variantOptions(variant, optionCount);

        for (let i = 0; i < optionCount; i++) {
          if (options[i] !== selections[i]) return false;
        }

        return true;
      }) || null
    );
  }

  /*
   * IMPORTANT:
   * An option is filtered ONLY by selections that come before it.
   *
   * Example:
   *   Option 2 is filtered by Option 1 only.
   *   Option 3 is filtered by Options 1 + 2 only.
   *
   * A stale/downstream Option 3 selection can therefore never hide a valid
   * Option 2 value.
   *
   * Availability is intentionally NOT checked here. Existing sold-out
   * combinations remain visible; only combinations that do not exist are hidden.
   */
  function buildAllowed(variants, selections, position, optionCount) {
    const allowed = new Set();

    // Do not populate a downstream dropdown until every upstream choice is made.
    for (let i = 0; i < position; i++) {
      if (!selections[i]) return allowed;
    }

    variants.forEach(variant => {
      const options = variantOptions(variant, optionCount);

      for (let i = 0; i < position; i++) {
        if (options[i] !== selections[i]) return;
      }

      if (options[position]) {
        allowed.add(options[position]);
      }
    });

    return allowed;
  }

  function getOptionName(select, index) {
    const name = select.getAttribute("name") || "";
    const match = name.match(/^options\[(.*)\]$/);

    if (match && match[1]) {
      return match[1];
    }

    if (select.id) {
      try {
        const label = document.querySelector(`label[for="${CSS.escape(select.id)}"]`);
        const labelText = label?.textContent?.trim();
        if (labelText) return labelText;
      } catch {
        // Fall through to generic option name.
      }
    }

    return `Option ${index + 1}`;
  }

  function ensurePlaceholder(select, index) {
    let placeholder = Array.from(select.options).find(option => option.value === "");

    if (!placeholder) {
      placeholder = document.createElement("option");
      placeholder.value = "";
      select.insertBefore(placeholder, select.firstChild);
    }

    placeholder.textContent = `Select ${getOptionName(select, index)}`;
    placeholder.hidden = false;
    placeholder.disabled = false;
    placeholder.style.display = "";

    return placeholder;
  }

  function ensurePlaceholders(selects) {
    selects.forEach((select, index) => ensurePlaceholder(select, index));
  }

  /*
   * Do not remove Shopify's option nodes. Hide + disable invalid values so they
   * can be restored when an upstream choice changes.
   *
   * Returns true if the current value had to be cleared.
   */
  function applyOptionVisibility(select, allowed, upstreamReady = true) {
    let wasCleared = false;

    Array.from(select.options).forEach(option => {
      if (option.value === "") {
        option.hidden = false;
        option.disabled = false;
        option.style.display = "";
        return;
      }

      const ok = upstreamReady && allowed.has(option.value);
      option.disabled = !ok;
      option.hidden = !ok;
      option.style.display = ok ? "" : "none";
    });

    const current = select.value;

    if (current) {
      const stillValid = Array.from(select.options).some(
        option => option.value === current && !option.disabled
      );

      if (!stillValid) {
        select.value = "";
        wasCleared = true;
      }
    }

    // Keep downstream selectors visibly inactive until the required earlier
    // selections have been made.
    select.disabled = !upstreamReady;

    return wasCleared;
  }

  function closestProductScope(root) {
    return (
      root.closest("product-info") ||
      root.closest(".shopify-section") ||
      root.closest("section") ||
      root.closest("main") ||
      document
    );
  }

  function getProductForms(root) {
    const scope = closestProductScope(root);
    return Array.from(scope.querySelectorAll('form[action*="/cart/add"]'));
  }

  function setAddButtonState(root, variant) {
    const forms = getProductForms(root);

    forms.forEach(form => {
      const addButton =
        form.querySelector('button[name="add"]') ||
        form.querySelector('button[type="submit"]');

      if (!addButton) return;

      const canAdd = !!variant && !!variant.available;

      if (canAdd) {
        addButton.removeAttribute("disabled");
        addButton.setAttribute("aria-disabled", "false");
        addButton.classList.remove("disabled");
      } else {
        addButton.setAttribute("disabled", "disabled");
        addButton.setAttribute("aria-disabled", "true");
        addButton.classList.add("disabled");
      }
    });
  }

  function hardSyncVariantId(root, variant) {
    if (!variant || !variant.id) return;

    const forms = getProductForms(root);

    forms.forEach(form => {
      const idInput = form.querySelector('input[name="id"]');

      if (idInput) {
        idInput.value = String(variant.id);
        idInput.dispatchEvent(new Event("input", { bubbles: true }));
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    setAddButtonState(root, variant);
  }

  function scheduleSync(root) {
    if (syncTimer) clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      const variants = getVariants(root);
      if (!variants || !Array.isArray(variants)) return;

      const selects = getSelects(root);
      if (!selects.length) return;

      const selections = getSelections(selects);

      if (!isFullSelection(selections)) {
        setAddButtonState(root, null);
        return;
      }

      const exact = findExactVariant(variants, selections, selects.length);

      if (exact) {
        hardSyncVariantId(root, exact);
      } else {
        setAddButtonState(root, null);
      }
    }, 140);
  }

  function clearDownstreamSelections(selects, changedIndex) {
    if (changedIndex === null || changedIndex < 0) return [];

    const cleared = [];

    for (let i = changedIndex + 1; i < selects.length; i++) {
      if (selects[i].value !== "") {
        selects[i].value = "";
        cleared.push(selects[i]);
      }
    }

    return cleared;
  }

  function refreshVisibility(root, selects, variants) {
    const optionCount = selects.length;
    let selections = getSelections(selects);
    const clearedByFiltering = [];

    selects.forEach((select, index) => {
      const upstreamReady =
        index === 0 || selections.slice(0, index).every(value => !!value);

      const allowed = buildAllowed(
        variants,
        selections,
        index,
        optionCount
      );

      if (applyOptionVisibility(select, allowed, upstreamReady)) {
        clearedByFiltering.push(select);
        selections = getSelections(selects);
      }
    });

    if (clearedByFiltering.length) {
      isInternalChange = true;

      try {
        dispatchAll(clearedByFiltering);
      } finally {
        isInternalChange = false;
      }
    }
  }

  function ensureReset(root, selects, variants) {
    if (selects.length < 2) return;

    let button = root.querySelector(`#${RESET_ID}`);

    if (!button) {
      injectResetStyles();

      button = document.createElement("button");
      button.type = "button";
      button.id = RESET_ID;
      button.innerHTML = `
        <span class="tmr-reset-label">Reset</span>
        <span class="tmr-reset-sub">selections</span>
      `;

      button.addEventListener("click", () => {
        isInternalChange = true;

        try {
          selects.forEach(select => {
            select.value = "";
          });

          // Tell Dawn that there is deliberately no active variant.
          dispatchAll(selects);
        } finally {
          isInternalChange = false;
        }

        refreshVisibility(root, selects, variants);
        setAddButtonState(root, null);
      });

      root.appendChild(button);
    }
  }

  function initializeBlankState(root, selects, variants) {
    if (root.hasAttribute(INIT_ATTR)) return;

    root.setAttribute(INIT_ATTR, "true");
    ensurePlaceholders(selects);

    isInternalChange = true;

    try {
      // Require the customer to explicitly choose every product option.
      selects.forEach(select => {
        select.value = "";
      });

      // Make Dawn recognize the intentionally incomplete selection.
      dispatchAll(selects);
    } finally {
      isInternalChange = false;
    }

    refreshVisibility(root, selects, variants);
    setAddButtonState(root, null);
  }

  function run(root, changedIndex = null) {
    const variants = getVariants(root);
    if (!variants || !Array.isArray(variants) || variants.length === 0) return;

    const selects = getSelects(root);
    if (!selects.length) return;

    ensurePlaceholders(selects);

    /*
     * If an upstream option changes, intentionally clear every downstream
     * selection. This prevents an old child selection from influencing the new
     * path and requires the customer to explicitly choose the new values.
     */
    const cleared = clearDownstreamSelections(selects, changedIndex);

    if (cleared.length) {
      isInternalChange = true;

      try {
        dispatchAll(cleared);
      } finally {
        isInternalChange = false;
      }
    }

    refreshVisibility(root, selects, variants);

    const selections = getSelections(selects);
    const exact = findExactVariant(variants, selections, selects.length);

    if (exact) {
      setTimeout(() => hardSyncVariantId(root, exact), 40);
      scheduleSync(root);
    } else {
      // Incomplete or impossible = never leave Add to Cart active.
      setAddButtonState(root, null);
      scheduleSync(root);
    }

    ensureReset(root, selects, variants);
  }

  document.addEventListener("change", event => {
    if (isInternalChange) return;

    const root = event.target.closest("variant-selects");
    if (!root) return;

    const selects = getSelects(root);
    const changedIndex = selects.indexOf(event.target);

    if (changedIndex < 0) return;

    run(root, changedIndex);
  });

  function initAll() {
    document.querySelectorAll("variant-selects").forEach(root => {
      const variants = getVariants(root);
      if (!variants || !Array.isArray(variants) || variants.length === 0) return;

      const selects = getSelects(root);
      if (!selects.length) return;

      initializeBlankState(root, selects, variants);
      run(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();